import { getGroupedSubgenres, type SubgenreType } from './subgenreRegistry'

export interface SubgenreEntry {
  id: string
  name: string
  slug: string
  definition: string
  bookCount: number
  /** Present when the entry comes from the canonical registry. */
  type?: SubgenreType
  /** Other parent genres this crossover also appears under. */
  secondaryGenres?: string[]
}

export interface GenreGlossaryEntry {
  id: string
  name: string
  slug: string
  definition: string
  bookCount: number
  colorKey: string
  subgenres: SubgenreEntry[]
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/**
 * Reader-facing definitions keyed by the CANONICAL display label in
 * data/subgenreRegistry.ts. The registry owns which subgenres exist and how
 * they are grouped; this map only supplies the editorial copy and book counts.
 * A registry entry with no definition here still renders, with a fallback line.
 */
const SUBGENRE_DEFINITIONS: Record<
  string,
  { definition: string; bookCount: number }
> = {
  // ── Romance ────────────────────────────────────────────────
  'Contemporary Romance': {
    definition:
      'Romance set in the present day, dealing with modern themes, careers, and relationship dynamics.',
    bookCount: 4200,
  },
  'Historical Romance': {
    definition:
      'Romance set in a past era, often featuring societal rules, historical events, and period-accurate settings.',
    bookCount: 3100,
  },
  'Romantic Comedy': {
    definition:
      'Lighthearted, humorous romance focusing on funny situations, witty banter, and entertaining misunderstandings.',
    bookCount: 1500,
  },
  'Romantic Suspense': {
    definition:
      'Romance intertwined with a thriller or mystery plot, where the protagonists face danger while falling in love.',
    bookCount: 1200,
  },
  'Dark Romance': {
    definition:
      'Romance exploring taboo, dangerous, or morally grey themes, often involving anti-heroes and psychological intensity.',
    bookCount: 1850,
  },
  'Erotic Romance': {
    definition:
      'Romance where explicit intimacy is central to the emotional arc rather than incidental to it.',
    bookCount: 1400,
  },
  'New Adult Romance': {
    definition:
      'Romance following protagonists in their late teens to mid-twenties, navigating independence, identity, and first serious love.',
    bookCount: 1150,
  },
  'Fantasy Romance': {
    definition:
      'Romance set in a magical world, where the relationship and the fantasy plot carry equal weight.',
    bookCount: 2600,
  },
  'Sci-Fi Romance': {
    definition:
      'Romance set in futuristic, technological, or space-based worlds.',
    bookCount: 780,
  },
  'Paranormal Romance': {
    definition:
      'Romance featuring supernatural elements or mythical beings such as vampires, shifters, or witches.',
    bookCount: 2100,
  },
  'Horror Romance': {
    definition:
      'Romance where genuine dread and horror imagery sit alongside the love story rather than being softened.',
    bookCount: 620,
  },
  'Horror Erotica': {
    definition:
      'Explicit romance fused with sustained horror, where fear and desire are deliberately entangled.',
    bookCount: 340,
  },
  'Gothic Romance': {
    definition:
      'Atmospheric romance featuring isolated settings, brooding love interests, and psychological menace.',
    bookCount: 850,
  },
  'Small Town Romance': {
    definition:
      'Romance rooted in a tight-knit community, where local ties and shared history shape the relationship.',
    bookCount: 1750,
  },
  'Sports Romance': {
    definition:
      'Romance centred on athletes, teams, or the pressures of competitive sport.',
    bookCount: 950,
  },
  'Billionaire Romance': {
    definition:
      'Romance built around extreme wealth, power imbalance, and the worlds that money opens or closes.',
    bookCount: 1080,
  },
  'Mafia Romance': {
    definition:
      'Romance set inside organised crime, where loyalty, violence, and protection drive the relationship.',
    bookCount: 890,
  },
  'MC Romance': {
    definition:
      'Romance set within motorcycle club culture, centred on brotherhood, territory, and outlaw codes.',
    bookCount: 540,
  },
  'Military Romance': {
    definition:
      'Romance involving serving or former military characters, often exploring duty, separation, and homecoming.',
    bookCount: 720,
  },
  'Workplace Romance': {
    definition:
      'Romance that develops between colleagues, navigating professional stakes and proximity.',
    bookCount: 830,
  },
  'College Romance': {
    definition:
      'Romance set on campus, shaped by coursework, roommates, and the transition into adulthood.',
    bookCount: 910,
  },
  'Monster Romance': {
    definition:
      'Romance between a human and a non-human being, where the monstrous is desired rather than defeated.',
    bookCount: 680,
  },
  'Alien Romance': {
    definition:
      'Romance between humans and extraterrestrials, often involving abduction, first contact, or fated bonds.',
    bookCount: 460,
  },
  Omegaverse: {
    definition:
      'Romance built on the alpha/beta/omega dynamic — a full world-system with its own biology, hierarchy, and mating conventions rather than a variation on another subgenre.',
    bookCount: 520,
  },
  'Pitch Black Romance': {
    definition:
      'The darkest tier of romance, where no redemption arc is guaranteed and content warnings are essential reading. Usually buried under Dark Romance elsewhere — here it is a shelf in its own right.',
    bookCount: 410,
  },
  'Taboo Romance': {
    definition:
      'Romance that deliberately crosses social or moral lines, exploring forbidden pairings and power imbalance. Listed separately rather than folded into Dark Romance.',
    bookCount: 560,
  },
  'Stalker Romance': {
    definition:
      'Romance built on obsessive pursuit, where surveillance and fixation are the engine of the relationship.',
    bookCount: 390,
  },
  'Captive Romance': {
    definition:
      'Romance where one partner is held by the other, exploring captivity, dependency, and shifting control.',
    bookCount: 370,
  },
  'Psychological Romance': {
    definition:
      'Romance driven by interior states — manipulation, obsession, and unreliable perception over external plot.',
    bookCount: 430,
  },

  // ── Fantasy ────────────────────────────────────────────────
  'Epic Fantasy': {
    definition:
      'Large-scale fantasy with high stakes, expansive world-building, and often a quest to save the world.',
    bookCount: 2800,
  },
  'High Fantasy': {
    definition:
      'Fantasy set entirely in a secondary world with its own rules, physical laws, and peoples.',
    bookCount: 2100,
  },
  'Low Fantasy': {
    definition:
      'Fantasy where magic is rare, marginal, or intrudes on an otherwise realistic world.',
    bookCount: 640,
  },
  'Urban Fantasy': {
    definition:
      'Fantasy set in a contemporary world where magic and magical creatures exist alongside ordinary society.',
    bookCount: 2400,
  },
  'Dark Fantasy': {
    definition:
      'Fantasy incorporating frightening or horrific themes, morally ambiguous characters, and bleak settings.',
    bookCount: 1600,
  },
  'Cozy Fantasy': {
    definition:
      'Low-stakes fantasy prioritising comfort, community, and everyday magic over world-ending conflict.',
    bookCount: 720,
  },
  'Sword & Sorcery': {
    definition:
      'Action-focused fantasy following heroic protagonists through personal battles rather than world-saving quests.',
    bookCount: 850,
  },
  'Mythic Fantasy': {
    definition:
      'Fantasy drawing directly on myth and legend, retelling or extending traditional pantheons and epics.',
    bookCount: 590,
  },
  'YA Fantasy': {
    definition:
      'Fantasy with teenage protagonists navigating magical worlds, emerging powers, or inherited destinies.',
    bookCount: 3100,
  },
  'Historical Fantasy': {
    definition:
      'Fantasy set in a recognisable historical period where magic operates alongside real events.',
    bookCount: 780,
  },
  Grimdark: {
    definition:
      'Gritty, violent, pessimistic fantasy following flawed characters through a harsh and amoral world.',
    bookCount: 750,
  },
  'Progression Fantasy': {
    definition:
      'Fantasy structured around a protagonist measurably growing in power through training and systems.',
    bookCount: 610,
  },
  LitRPG: {
    definition:
      'Fantasy where game mechanics — levels, stats, quests — are explicit, diegetic parts of the world.',
    bookCount: 540,
  },
  'Portal Fantasy': {
    definition:
      'Fantasy in which characters cross from our world into another through a gateway.',
    bookCount: 480,
  },
  'Gaslamp Fantasy': {
    definition:
      'Fantasy set in a Victorian or Edwardian milieu, blending gaslight-era manners with occult or magical elements.',
    bookCount: 320,
  },

  // ── Horror ─────────────────────────────────────────────────
  'Psychological Horror': {
    definition:
      "Horror relying on characters' fears, guilt, and mental instability rather than monsters or gore.",
    bookCount: 1200,
  },
  'Supernatural Horror': {
    definition:
      'Horror featuring ghosts, demons, or other paranormal entities that defy scientific explanation.',
    bookCount: 1600,
  },
  'Cosmic Horror': {
    definition:
      'Horror emphasising human insignificance before vast, incomprehensible, indifferent forces.',
    bookCount: 650,
  },
  'Folk Horror': {
    definition:
      'Horror drawing on folklore, rural isolation, and ancient traditions or pagan belief.',
    bookCount: 550,
  },
  'Gothic Horror': {
    definition:
      'Atmospheric horror set in decaying mansions, ruined castles, and other places heavy with history.',
    bookCount: 850,
  },
  'Body Horror': {
    definition:
      'Horror focused on the grotesque violation, mutation, or destruction of the human body.',
    bookCount: 450,
  },
  'Creature Horror': {
    definition:
      'Horror driven by a monstrous animal or being hunting the cast.',
    bookCount: 480,
  },
  'Haunted House': {
    definition:
      'Horror where a building itself is the antagonist, trapping and unravelling its occupants.',
    bookCount: 620,
  },
  'Survival Horror': {
    definition:
      'Horror centred on endurance and scarcity, where escape matters more than explanation.',
    bookCount: 530,
  },
  'Extreme Horror': {
    definition:
      'Horror that deliberately pushes past mainstream limits of violence and transgression.',
    bookCount: 290,
  },
  Splatterpunk: {
    definition:
      'Graphic, visceral horror rejecting restraint in favour of explicit gore and confrontation.',
    bookCount: 260,
  },
  'Occult Horror': {
    definition:
      'Horror concerning ritual, possession, secret orders, and forbidden knowledge.',
    bookCount: 440,
  },
  Slasher: {
    definition:
      'Horror following a killer stalking a sequence of victims, often in an isolated location.',
    bookCount: 500,
  },

  // ── Thriller ───────────────────────────────────────────────
  'Psychological Thriller': {
    definition:
      'Suspense driven by unstable mental states, paranoia, and deceptive characters.',
    bookCount: 2200,
  },
  'Domestic Thriller': {
    definition:
      'Suspense focused on family secrets and danger inside the home and marriage.',
    bookCount: 1100,
  },
  'Crime Thriller': {
    definition:
      'Fast-moving stories following criminals, heists, or the pursuit of dangerous offenders.',
    bookCount: 1800,
  },
  'Spy Thriller': {
    definition:
      'Thrillers of espionage, intelligence agencies, secret missions, and international intrigue.',
    bookCount: 1200,
  },
  'Techno Thriller': {
    definition:
      'Thrillers where technology, engineering, or cyberwarfare drives the threat and the solution.',
    bookCount: 690,
  },
  'Medical Thriller': {
    definition:
      'Thrillers set around outbreaks, unethical research, or medical professionals in danger.',
    bookCount: 650,
  },
  'Legal Thriller': {
    definition:
      'Thrillers where lawyers and the justice system are central to the danger and the resolution.',
    bookCount: 850,
  },
  'Political Thriller': {
    definition:
      'Thrillers of power struggles, government conspiracy, and national security threats.',
    bookCount: 950,
  },
  'Supernatural Thriller': {
    definition:
      'Suspense with a paranormal cause, pitched to unsettle rather than to horrify outright.',
    bookCount: 740,
  },
  'Survival Thriller': {
    definition:
      'Thrillers where the antagonist is the environment and the goal is simply to live.',
    bookCount: 480,
  },

  // ── Science Fiction ────────────────────────────────────────
  'Space Opera': {
    definition:
      'Large-scale adventures across space, with political conflict, exploration, or interstellar war.',
    bookCount: 1800,
  },
  Cyberpunk: {
    definition:
      'High-tech futures where advanced technology coexists with social collapse and corporate dominance.',
    bookCount: 1200,
  },
  'Military Sci-Fi': {
    definition:
      'Science fiction focused on armed forces, tactics, and conflict in futuristic settings.',
    bookCount: 850,
  },
  'Hard Sci-Fi': {
    definition:
      'Science fiction prioritising scientific accuracy and rigour in its speculation.',
    bookCount: 950,
  },
  'Dystopian Sci-Fi': {
    definition:
      'Stories set in oppressive or ruined future societies built on control and scarcity.',
    bookCount: 2100,
  },
  'Post-Apocalyptic': {
    definition:
      'Stories after a catastrophe has ended modern civilisation, focused on survival and rebuilding.',
    bookCount: 1650,
  },
  'Time Travel': {
    definition:
      'Stories built on moving through time and the consequences of altering it.',
    bookCount: 1100,
  },
  'Sci-Fi Thriller': {
    definition:
      'Science fiction structured as suspense, where the speculative element is the threat.',
    bookCount: 720,
  },
  'First Contact': {
    definition:
      'Stories of humanity’s initial encounter with non-human intelligence.',
    bookCount: 560,
  },
  'Climate Fiction': {
    definition:
      'Fiction confronting climate change, ecological collapse, and adaptation.',
    bookCount: 430,
  },
  Biopunk: {
    definition:
      'Science fiction of genetic engineering, biotech corporations, and modified bodies.',
    bookCount: 280,
  },

  // ── Mystery ────────────────────────────────────────────────
  'Cozy Mystery': {
    definition:
      'Lighthearted mysteries with an amateur sleuth, a small community, and violence kept off the page.',
    bookCount: 2400,
  },
  'Police Procedural': {
    definition:
      'Mysteries depicting investigative procedure, forensics, and the working routine of law enforcement.',
    bookCount: 1500,
  },
  'Amateur Sleuth': {
    definition:
      'Mysteries solved by someone with no professional standing, working from curiosity and access.',
    bookCount: 980,
  },
  'Hard-Boiled Mystery': {
    definition:
      'Cynical, morally weathered detective fiction set in a corrupt world.',
    bookCount: 610,
  },
  'Historical Mystery': {
    definition:
      'Crime-solving set in a past era, without the benefit of modern forensics.',
    bookCount: 950,
  },
  Noir: {
    definition:
      'Fatalistic crime fiction where the protagonist is compromised and the ending offers no relief.',
    bookCount: 520,
  },
  'Literary Mystery': {
    definition:
      'Mysteries where prose, character, and theme carry as much weight as the puzzle.',
    bookCount: 470,
  },
  'Supernatural Mystery': {
    definition:
      'Investigations where the solution involves forces outside natural explanation.',
    bookCount: 540,
  },
}

/** Glossary-only entries that are not (yet) part of the canonical registry. */
const EXTRA_SUBGENRES: Record<string, SubgenreEntry[]> = {
  fantasy: [
    {
      id: 'fantasy-magical-realism',
      name: 'Magical Realism',
      slug: 'magical-realism',
      definition:
        'Stories set in the mundane world where magical elements are treated as ordinary.',
      bookCount: 950,
    },
    {
      id: 'fantasy-fairy-tale-retellings',
      name: 'Fairy Tale Retellings',
      slug: 'fairy-tale-retellings',
      definition:
        'Reimagined versions of classic fairy tales, often shifting perspective or adding new twists.',
      bookCount: 1100,
    },
  ],
  scifi: [
    {
      id: 'scifi-steampunk',
      name: 'Steampunk',
      slug: 'steampunk',
      definition:
        'Retro-futuristic science fiction built on 19th-century steam technology, often in an alternate Victorian era.',
      bookCount: 750,
    },
  ],
}

interface GenreMeta {
  id: string
  name: string
  slug: string
  definition: string
  bookCount: number
  colorKey: string
  /** Key into SUBGENRE_REGISTRIES. Omit for genres the registry does not cover. */
  registryGenre?: string
  /** Used verbatim when there is no registryGenre. */
  subgenres?: SubgenreEntry[]
}

const GENRE_META: GenreMeta[] = [
  {
    id: 'romance',
    name: 'Romance',
    slug: 'romance',
    definition:
      'Stories primarily focused on the romantic relationship between two or more people, ending with an emotionally satisfying and optimistic conclusion.',
    bookCount: 12450,
    colorKey: 'Romance',
    registryGenre: 'Romance',
  },
  {
    id: 'fantasy',
    name: 'Fantasy',
    slug: 'fantasy',
    definition:
      'Speculative fiction featuring magical elements, imaginary worlds, supernatural creatures, and phenomena that do not exist in the real world.',
    bookCount: 9800,
    colorKey: 'Fantasy',
    registryGenre: 'Fantasy',
  },
  {
    id: 'scifi',
    name: 'Science Fiction',
    slug: 'science-fiction',
    definition:
      'Stories exploring speculative technology, futuristic societies, space travel, or scientific possibilities.',
    bookCount: 7600,
    colorKey: 'Science Fiction',
    registryGenre: 'Science Fiction',
  },
  {
    id: 'mystery',
    name: 'Mystery',
    slug: 'mystery',
    definition:
      'Stories centered around solving a crime, puzzle, or uncovering a secret, typically following an investigator or amateur sleuth.',
    bookCount: 8200,
    colorKey: 'Mystery',
    registryGenre: 'Mystery',
  },
  {
    id: 'horror',
    name: 'Horror',
    slug: 'horror',
    definition:
      'Fiction designed to frighten, scare, disgust, or startle readers by inducing feelings of horror and terror.',
    bookCount: 4500,
    colorKey: 'Horror',
    registryGenre: 'Horror',
  },
  {
    id: 'thriller',
    name: 'Thriller',
    slug: 'thriller',
    definition:
      'Fast-paced, suspenseful stories characterized by high stakes, tension, excitement, and anticipation.',
    bookCount: 6800,
    colorKey: 'Thriller',
    registryGenre: 'Thriller',
  },
  {
    id: 'historical',
    name: 'Historical',
    slug: 'historical',
    definition:
      'Stories set in the past, carefully recreating the social conditions, events, and atmosphere of a specific historical period.',
    bookCount: 5200,
    colorKey: 'Historical',
    subgenres: [
      {
        id: 'hist-fiction',
        name: 'Historical Fiction',
        slug: 'historical-fiction',
        definition:
          'Narratives set in the past that pay close attention to historical accuracy, sometimes featuring real historical figures.',
        bookCount: 2800,
      },
      {
        id: 'alt-history',
        name: 'Alternate History',
        slug: 'alternate-history',
        definition:
          'Stories exploring what might have happened if a specific historical event had a different outcome.',
        bookCount: 850,
      },
      {
        id: 'hist-literary',
        name: 'Historical Literary',
        slug: 'historical-literary',
        definition:
          'Historical fiction where prose style and interiority take precedence over incident.',
        bookCount: 720,
      },
      {
        id: 'hist-adventure',
        name: 'Historical Adventure',
        slug: 'historical-adventure',
        definition:
          'Action-driven stories set in the past, built around voyages, campaigns, and exploration.',
        bookCount: 640,
      },
    ],
  },
  {
    id: 'young-adult',
    name: 'Young Adult',
    slug: 'young-adult',
    definition:
      'Literature written for, published for, or marketed to adolescents and young adults, typically featuring teenage protagonists.',
    bookCount: 8500,
    colorKey: 'YoungAdult',
    subgenres: [
      {
        id: 'ya-contemp',
        name: 'YA Contemporary',
        slug: 'ya-contemporary',
        definition:
          'Realistic stories set in the modern day focusing on teenage experiences, coming of age, school, and relationships.',
        bookCount: 2600,
      },
      {
        id: 'ya-fantasy',
        name: 'YA Fantasy',
        slug: 'ya-fantasy',
        definition:
          'Fantasy stories featuring teenage protagonists navigating magical worlds, discovering powers, or fulfilling destinies.',
        bookCount: 3100,
      },
      {
        id: 'ya-dystopian',
        name: 'YA Dystopian',
        slug: 'ya-dystopian',
        definition:
          'Stories where teenage protagonists rebel against oppressive future societies or fight for survival in ruined worlds.',
        bookCount: 1800,
      },
      {
        id: 'ya-romance',
        name: 'YA Romance',
        slug: 'ya-romance',
        definition:
          'Stories centered on first love, teenage relationships, and the emotional journey of adolescent romance.',
        bookCount: 2200,
      },
      {
        id: 'ya-mystery',
        name: 'YA Mystery',
        slug: 'ya-mystery',
        definition:
          'Mysteries or thrillers featuring teenage sleuths solving crimes, uncovering secrets, or navigating dangerous situations.',
        bookCount: 1100,
      },
    ],
  },
  {
    id: 'non-fiction',
    name: 'Non-Fiction',
    slug: 'non-fiction',
    definition:
      'Informative or factual writing based on real events, real people, facts, and history rather than imagination.',
    bookCount: 4200,
    colorKey: 'NonFiction',
    subgenres: [
      {
        id: 'memoir',
        name: 'Memoir',
        slug: 'memoir',
        definition:
          'A narrative written from the perspective of the author about an important part of their life or a specific theme.',
        bookCount: 1200,
      },
      {
        id: 'biography',
        name: 'Biography',
        slug: 'biography',
        definition:
          "A detailed description of a person's life, written by someone else, covering their experiences and achievements.",
        bookCount: 950,
      },
      {
        id: 'self-help',
        name: 'Self-Help',
        slug: 'self-help',
        definition:
          'Books written with the intention to instruct readers on solving personal problems or improving aspects of their lives.',
        bookCount: 1500,
      },
      {
        id: 'true-crime',
        name: 'True Crime',
        slug: 'true-crime',
        definition:
          'Non-fiction examining actual crimes, detailing the events, investigations, and people involved.',
        bookCount: 850,
      },
      {
        id: 'history',
        name: 'History',
        slug: 'history',
        definition:
          'Factual accounts and analyses of past events, eras, civilizations, or historical movements.',
        bookCount: 1100,
      },
      {
        id: 'science',
        name: 'Science',
        slug: 'science',
        definition:
          'Books explaining scientific concepts, discoveries, theories, or the natural world to a general audience.',
        bookCount: 750,
      },
      {
        id: 'travel',
        name: 'Travel',
        slug: 'travel',
        definition:
          "Literature documenting travel experiences, exploring different cultures, places, and the author's journey.",
        bookCount: 600,
      },
    ],
  },
]

const buildSubgenres = (meta: GenreMeta): SubgenreEntry[] => {
  if (!meta.registryGenre) return meta.subgenres || []
  const fromRegistry = getGroupedSubgenres(meta.registryGenre).flatMap((group) =>
    group.entries.map((entry) => {
      const authored = SUBGENRE_DEFINITIONS[entry.displayLabel]
      return {
        id: `${meta.id}-${slugify(entry.displayLabel)}`,
        name: entry.displayLabel,
        slug: slugify(entry.displayLabel),
        definition:
          authored?.definition ||
          `A ${group.label.toLowerCase()} subgenre of ${meta.name}.`,
        bookCount: authored?.bookCount ?? 0,
        type: entry.type,
        secondaryGenres: entry.secondaryGenres,
      }
    }),
  )
  return [...fromRegistry, ...(EXTRA_SUBGENRES[meta.id] || [])]
}

export const genreGlossaryData: GenreGlossaryEntry[] = GENRE_META.map(
  (meta) => ({
    id: meta.id,
    name: meta.name,
    slug: meta.slug,
    definition: meta.definition,
    bookCount: meta.bookCount,
    colorKey: meta.colorKey,
    subgenres: buildSubgenres(meta),
  }),
)

